import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCircleCheck,
  faClipboardCheck,
  faCloudCheck,
  faCommentQuestion,
  faKey,
  faLock,
  faMoneyCheckDollarPen,
  faMonitorWaveform,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";

import { ExternalLink } from "~/components/site/external-link";
import { item, Layout } from "~/components/site/layout";
import { ROUTE_DATA } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";

const { desc, path, title } = ROUTE_DATA.security;

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data ? getMeta({ desc, title, urls: data.urls }) : [];
};

type Content = Readonly<{
  icon: IconDefinition;
  title: string;
  text: JSX.Element;
}>;

const blurbs: Content[] = [
  {
    icon: faCloudCheck,
    title: "Cloud Security",
    text: (
      <p className="blurb m-0">
        We follow cloud-native security best practices, implement continuous
        code delivery, system and network monitoring, and vulnerability
        assessments. Changes are highly automated and require strong
        authentication.
      </p>
    ),
  },
  {
    icon: faMoneyCheckDollarPen,
    title: "Asset Security",
    text: (
      <p className="blurb m-0">
        Our multi-party control eliminates single points of failure and improves
        resilience against loss or compromise of individual private keys.
        Multiple signers using cold wallets are required to perform all
        sensitive functions.
      </p>
    ),
  },

  {
    icon: faLock,
    title: "Identity & Access Controls",
    text: (
      <p className="blurb m-0">
        Multi-factor authentication (MFA) and strong password policies ensure
        access to cloud services are protected. Access to production systems
        require phishing-resistant hardware security keys.
      </p>
    ),
  },
  {
    icon: faClipboardCheck,
    title: "Third-Party Audits",
    text: (
      <p className="blurb m-0">
        Our organization undergoes independent third-party assessments to test
        our security. Our smart contracts are audited by{" "}
        <ExternalLink href="https://www.certik.com/projects/brale">
          CertiK
        </ExternalLink>
        .
      </p>
    ),
  },
  {
    icon: faMonitorWaveform,
    title: "Monitoring & Risk Management",
    text: (
      <p className="blurb m-0">
        Brale conducts regular risk assessments, reviewing and updating our
        security policies as needed. We utilize continuous monitoring to
        identify and respond to events and audit logs allow us to reconstruct
        events after they occur.
      </p>
    ),
  },
  {
    icon: faKey,
    title: "Encryption Key Management",
    text: (
      <p className="blurb m-0">
        We use envelope encryption backed by key management service (KMS)
        hardware security modules (HSMs) and automated quarterly key rotation to
        keep your data safe.
      </p>
    ),
  },
  {
    icon: faCircleCheck,
    title: "Organizational Security",
    text: (
      <p className="blurb m-0">
        Our Information Security Program follows the guidance set forth by the
        SOC2 Security Trust Services Criteria. All employees undergo rigorous
        background checks and are subject to ongoing screening and security
        training throughout their employment.
      </p>
    ),
  },

  {
    icon: faCommentQuestion,
    title: "Contact Us",
    text: (
      <p className="blurb m-0">
        If you have any questions, comments or concerns or if you wish to report
        a potential security issue, please contact{" "}
        <a className="link" href="mailto:security@brale.xyz">
          security@brale.xyz
        </a>
        . We believe in responsible disclosure and support it through a
        collaborative and communicative process with reporters.
      </p>
    ),
  },
];

export default function Security(): JSX.Element {
  return (
    <Layout>
      <section className="section-first">
        <div className="container grid lg:grid-cols-3 items-start gap-16">
          <motion.h1 variants={item} className="h2 m-0 lg:sticky top-8">
            Enterprise-grade security
          </motion.h1>
          <div className="col-span-2">
            <div className="flex flex-col col-span-2 gap-y-8">
              <div className="flex flex-col sm:grid sm:grid-cols-2 blurb-gap">
                {blurbs.map((c) => (
                  <motion.div variants={item} key={c.title}>
                    <FontAwesomeIcon
                      className="h-12 w-12 text-purple-500 mb-3"
                      icon={c.icon}
                    />
                    <h3 className="h4 mt-0 mb-3">{c.title}</h3>
                    {c.text}
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
